import { topics, useChannels } from "./channels.mjs";
import { mapProductListing } from "./products/mapper.mjs";
import { useAuth } from "./auth/index.mjs";
import {
  AddToWishlistDocument,
  AllWishlistItemsDocument,
  RemoveFromWishlistDocument,
  WishlistCountDocument,
  WishlistDocument
} from "../graphql/Wishlist.mjs";
import { extractValue, toNonNullable } from "../utils/collections.mjs";
import { get, set } from "idb-keyval";
import { useMutation, useQuery } from "villus";
import { computed, onMounted, reactive, ref, toRaw, watch } from "vue";
const WISHLIST_TRACKER = reactive({
  count: 0,
  lookup: {},
  initialized: false
});
const DB_KEY = "fav-skus";
const skus = ref([]);
export function useWishlist(variables) {
  const items = ref([]);
  const { data, error, isFetching, execute } = useQuery({
    query: WishlistDocument.toString(),
    variables,
    cachePolicy: "network-only"
  });
  watch(data, (value) => {
    if (!value) {
      return;
    }
    const vars = extractValue(variables);
    if (vars?.currentPage === 1) {
      items.value = mapWishlistItems(value);
      return;
    }
    items.value.push(...mapWishlistItems(value));
  });
  const totalCount = computed(() => {
    return data.value?.customer?.wishlists?.[0]?.totalCount || 0;
  });
  return {
    data,
    items,
    totalCount,
    error,
    isFetching,
    refetch: execute
  };
}
function mapWishlistItems(data) {
  if (!data) {
    return [];
  }
  return toNonNullable(data.customer?.wishlists?.[0]?.items_v2?.items).map(
    (item) => ({
      wishlistId: item.id,
      ...mapProductListing(item.product)
    })
  );
}
export function useAddToWishlist(sku) {
  const { user } = useAuth();
  const { execute } = useMutation(AddToWishlistDocument);
  async function _addToWishlist() {
    WISHLIST_TRACKER.lookup[sku] = WISHLIST_TRACKER.count + 1;
    const { data, error } = await execute({
      pageSize: WISHLIST_TRACKER.count + 100,
      // keep a buffer of more items
      wishlistId: user?.value?.wishlist.id,
      wishlistItems: Array.isArray(sku) ? sku.map((i) => ({ sku: i, quantity: 1 })) : [
        {
          sku,
          quantity: 1
        }
      ]
    });
    if (error) {
      throw error;
    }
    if (data?.response?.errors.length) {
      throw new Error(data?.response.errors[0]?.message);
    }
    updateTracker(data?.response?.wishlist);
  }
  async function _addItemGuestToWishlist() {
    await updateState(
      Array.isArray(sku) ? sku.map((i) => ({ sku: i, quantity: 1 })) : [...skus.value, { sku }]
    );
  }
  async function addToWishlist() {
    if (user?.value) {
      await _addToWishlist();
      return;
    }
    await _addItemGuestToWishlist();
  }
  return {
    addToWishlist
  };
}
export function useRemoveFromWishlist(sku) {
  const { user } = useAuth();
  const { execute } = useMutation(RemoveFromWishlistDocument);
  async function _removeFromWishlist() {
    const itemId = String(WISHLIST_TRACKER.lookup[sku]);
    delete WISHLIST_TRACKER.lookup[sku];
    const { data, error } = await execute({
      pageSize: WISHLIST_TRACKER.count + 100,
      // keep a buffer of more items
      wishlistId: user?.value?.wishlist.id,
      wishlistItemsIds: [itemId]
    });
    if (error) {
      throw error;
    }
    if (data?.response?.errors.length) {
      throw new Error(data?.response.errors[0]?.message);
    }
    updateTracker(data?.response?.wishlist);
  }
  async function _removeGuestFromWishlist() {
    await wishlistRemoveItem(sku);
  }
  async function removeFromWishlist() {
    if (user?.value) {
      await _removeFromWishlist();
      return;
    }
    await _removeGuestFromWishlist();
  }
  return {
    removeFromWishlist
  };
}
function _useInitWishlistTracker() {
  WISHLIST_TRACKER.initialized = true;
  const { data: countData } = useQuery({
    query: WishlistCountDocument.toString(),
    cachePolicy: "network-only"
  });
  const { data } = useQuery({
    query: AllWishlistItemsDocument.toString(),
    cachePolicy: "network-only",
    fetchOnMount: false,
    variables: computed(() => {
      if (!countData.value) {
        return {};
      }
      return {
        pageSize: countData.value?.customer?.wishlist.totalCount || 100
      };
    })
  });
  watch(data, (value) => {
    updateTracker(value?.customer?.wishlist);
  });
}
export function useAddExplicitToWishlist() {
  const { user } = useAuth();
  const { execute } = useMutation(AddToWishlistDocument);
  async function addToWishlist(sku) {
    const { data, error } = await execute({
      pageSize: WISHLIST_TRACKER.count + 100,
      // keep a buffer of more items
      wishlistId: user?.value?.wishlist.id,
      wishlistItems: Array.isArray(sku) ? sku.map((i) => ({ sku: i, quantity: 1 })) : [
        {
          sku,
          quantity: 1
        }
      ]
    });
    if (error) {
      throw error;
    }
    if (data?.response?.errors.length) {
      throw new Error(data?.response.errors[0]?.message);
    }
    updateTracker(data?.response?.wishlist);
  }
  return {
    addToWishlist
  };
}
function _useGuestInitWishlistTracker() {
  WISHLIST_TRACKER.initialized = true;
  const { subscribe } = useChannels();
  const { addToWishlist } = useAddExplicitToWishlist();
  const { user } = useAuth();
  onMounted(async () => {
    const items = await get(DB_KEY) || [];
    if (!items.length && !skus.value.length) {
      return;
    }
    skus.value = [...new Set(items)];
  });
  subscribe(topics.auth.identify, async () => {
    if (user.value) {
      await addToWishlist(skus.value.map((item) => item.sku));
      await clearItems();
    }
  });
}
function useInitWishlistTracker() {
  const { user } = useAuth();
  if (user.value) {
    _useInitWishlistTracker();
    return;
  }
  _useGuestInitWishlistTracker();
}
export function useIsInWishlist(sku) {
  const { user } = useAuth();
  if (!WISHLIST_TRACKER.initialized) {
    useInitWishlistTracker();
  }
  return computed(() => {
    return user?.value ? WISHLIST_TRACKER.lookup[sku] !== void 0 : !!skus.value.find((item) => item?.sku === sku);
  });
}
function updateTracker(data) {
  WISHLIST_TRACKER.count = data?.totalCount || 100;
  WISHLIST_TRACKER.lookup = data?.items_v2?.items?.reduce(
    (acc, item) => {
      const sku = item?.product?.sku;
      if (!sku || !item?.id) {
        return acc;
      }
      acc[sku] = Number(item?.id);
      return acc;
    },
    {}
  ) || {};
}
export function useIsInWhishlist(sku) {
  return computed(() => {
    return skus.value.some((item) => item.sku === sku);
  });
}
export async function wishlistCompareItem(sku) {
  if (skus.value.length === 0) {
    await updateState([...skus.value, sku]);
    return;
  }
  await updateState([...skus.value, sku]);
}
export async function wishlistRemoveItem(sku) {
  skus.value = skus.value.filter((item) => {
    return item.sku !== sku;
  });
  await updateState(skus.value);
}
export async function clearItems() {
  await updateState([]);
}
async function updateState(items) {
  const cleanedSkus = items.map((item) => toRaw(item));
  const uniqueSku = [...new Set(cleanedSkus)];
  await set(DB_KEY, uniqueSku);
  skus.value = uniqueSku;
}
